<script>
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexFormInput from '@/rex-components/rex-modal-input.vue';
import BaseIcon from '@/rex-components/BaseIcon.vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import Card from '@/rex-components/rex-card.vue';
import RexCardV3 from '@/rex-components/RexCardV3.vue';
import { mapActions } from 'vuex';
import {
  INSTALL_SERVICES_MANUALLY,
  UPDATE_SERVICE_DETAIL,
} from '@/store/modules/cloud/aws/aws.constants';
import { CloudService } from '@/services/api/integration.service';
import { notificationError, notificationSucess } from '@/utils/notification';
import { isEqual, isEmpty } from 'lodash';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import RexRadioV3 from '@/rex-components/RexRadioV3.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import RexSwitchV3 from '@/rex-components/RexSwitchV3.vue';
import { rdsServiceCreateEditFormRadioGroupOptions } from '@/components/Cloud/Dashboard/cloud.types';

export default {
  name: 'LambdaCreateEditForm',
  components: {
    RexFormInput,
    RexModalLabel,
    BaseIcon,
    Card,
    Toggle: RexSwitchV3,
    RexInfoLabel,
    RexRadioV3,
    RexSpinV3,
    RexButtonV3,
    RexCardV3,
  },
  props: {
    type: { type: String, required: true },
  },
  emits: { updateAllServices: null, close: null },
  beforeMount: function () {
    this.getInitialData();
  },
  computed: {
    isEdit() {
      return this.type === 'edit';
    },
    getCardBodyStyle() {
      return {
        height: '400px',
        width: '100%',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      };
    },
  },
  data() {
    return {
      userInputs: {
        pullFrequency: '',
        global: false,
        enableMonitoring: false,
        globalPullFrequencyMins: '',
        tagsCondition: '',
        tags: [{}],
      },
      initialValue: {
        pullFrequency: '',
        global: false,
        enableMonitoring: false,
        globalPullFrequencyMins: '',
        tags: [{}],
      },
      errorState: {
        isDatatypeError: false,
        isPullFrequencyValueError: false,
        isPullFrequencyValidationError: false,
      },
      isLoading: false,
      radioGroupOptions: rdsServiceCreateEditFormRadioGroupOptions,
    };
  },
  methods: {
    isEqual,
    ...mapActions('cloudState/aws', [
      INSTALL_SERVICES_MANUALLY,
      UPDATE_SERVICE_DETAIL,
    ]),
    handleMonitorToggeling(toggleValue) {
      this.userInputs.enableMonitoring = toggleValue;
    },
    handleIsGlobalToggling(toggleValue) {
      this.userInputs.global = toggleValue;
      this.userInputs.pullFrequency = this.userInputs.globalPullFrequencyMins;
    },
    arrayToObject(arr) {
      return arr.reduce((obj, item) => {
        obj[item] = true;
        return obj;
      }, {});
    },
    async submitFrom(submitAction, payload) {
      this.isLoading = true;
      try {
        const data = await this[submitAction](payload);

        if (data.success) {
          notificationSucess(
            this.type === 'edit'
              ? this.$t('aws.lambdaServiceUpdated')
              : this.$t('aws.awslambdaConfiguredSuccessfully'),
          );
          this.$emit(
            'updateAllServices',
            'lambda',
            this.userInputs.enableMonitoring,
          );
        } else {
          notificationError(
            data.message || this.$t('aws.formSubmissionFailed'),
          );
        }
      } catch (err) {
        const error = JSON.parse(JSON.stringify(err));
        notificationError(error.message || this.$t('aws.formSubmissionFailed'));
      } finally {
        this.isLoading = false;
      }
    },
    handleSaveClick() {
      if (!this.isFormValid()) return;
      if (this.type === 'edit') {
        this.submitFrom(UPDATE_SERVICE_DETAIL, {
          subscriptionId: this.$route.params.subscriptionId,
          serviceSlug: 'lambda',
          meta: {},
          pullFrequencyMins:
            this.userInputs.pullFrequency ||
            this.userInputs.globalPullFrequencyMins,
          isGlobalEnabled: this.userInputs.global,
          tagsCondition: this.userInputs.tagsCondition,
          isMonitoringEnabled: this.userInputs.enableMonitoring,
          tags:
            Object.keys(this.userInputs.tags[0]).length > 0
              ? this.userInputs.tags.filter((tag) => !isEmpty(tag))
              : [],
        });
      } else {
        this.submitFrom(INSTALL_SERVICES_MANUALLY, {
          isAutoInstall: false,
          serviceSlug: 'lambda',
          subscriptionId: this.$route.params.subscriptionId,
          meta: {},
          tags:
            Object.keys(this.userInputs.tags[0]).length > 0
              ? this.userInputs.tags.filter((tag) => !isEmpty(tag))
              : [],
          tagsCondition: this.userInputs.tagsCondition,
          pullFrequencyMins:
            this.userInputs.pullFrequency ||
            this.userInputs.globalPullFrequencyMins,
          isMonitoringEnabled: this.userInputs.enableMonitoring,
        });
      }
    },
    isFormValid() {
      this.errorState.isPullFrequencyValueError = false;
      this.errorState.isPullFrequencyValidationError = false;

      if (
        isNaN(this.userInputs.pullFrequency) ||
        this.userInputs.pullFrequency?.includes?.('.') ||
        this.userInputs.pullFrequency?.includes?.('-')
      ) {
        this.errorState.isPullFrequencyValidationError = true;
        return false;
      }

      if (
        this.userInputs.pullFrequency === '0' ||
        !this.userInputs.pullFrequency
      ) {
        this.errorState.isPullFrequencyValueError = true;
        return false;
      }

      return true;
    },
    async getInitialData() {
      try {
        if (this.type === 'edit') {
          const { data } = await CloudService.getServicesInfo({
            subscriptionId: this.$route.params.subscriptionId,
            service: 'lambda',
          });
          this.userInputs.pullFrequency = data.service.pullFrequencyMins;
          this.userInputs.enableMonitoring =
            data.service.isMonitoringEnabled === 1;
          this.userInputs.global =
            data.service.pullFrequencyMins ===
            data.service.globalPullFrequencyMins;
          this.userInputs.tagsCondition = data.service.tagsCondition;
          this.userInputs.globalPullFrequencyMins =
            data.service.globalPullFrequencyMins;
          this.userInputs.tags =
            data.service.tags.length > 0
              ? [...JSON.parse(JSON.stringify(data.service.tags)), {}]
              : [{}];

          this.initialValue = {
            ...this.userInputs,
            tags:
              data.service.tags.length > 0
                ? [...JSON.parse(JSON.stringify(data.service.tags)), {}]
                : [{}],
          };
        } else {
          const { data } = await CloudService.getGlobalFrequency({
            subscriptionId: this.$route.params.subscriptionId,
          });
          if (data.success) {
            this.userInputs.globalPullFrequencyMins =
              data.globalPullFrequencyMins;
          }
          this.userInputs.pullFrequency = data.globalPullFrequencyMins;
          this.userInputs.enableMonitoring = true;
          this.userInputs.global = true;
          this.userInputs.tagsCondition = 'AND';
        }
      } catch {
        notificationError(this.$t('aws.formSubmissionFailed'));
      }
    },
  },
};
</script>

<template>
  <div class="lambda-create-edit-form">
    <RexSpinV3 :spinning="isLoading">
      <RexCardV3 :bodyStyle="getCardBodyStyle">
        <template #title>
          <p class="card-header-text">
            <span v-if="isEdit">
              {{ $t('aws.awsLambdaEditForm') }}
            </span>
            <span v-else>
              {{ $t('aws.awsLambda') }}
            </span>
          </p>
        </template>
        <div class="card-body">
          <div class="form-container">
            <div class="input-container">
              <div class="pull-frequency-container">
                <RexModalLabel
                  :primaryLabelStyle="{
                    color: userInputs.global ? 'rgba(89, 90, 89, 1)' : 'white',
                  }"
                  :primaryLabel="$t('aws.rdsFormFields.pullFrequency')"
                />

                <div class="toggle-container">
                  {{ $t('aws.rdsFormFields.global') }}
                  <Toggle
                    @onChange="handleIsGlobalToggling"
                    v-model:checked="userInputs.global"
                  />
                </div>
              </div>
              <div class="input-field-container">
                <RexFormInput
                  v-model="userInputs.pullFrequency"
                  type="text"
                  id="input-pull-frequency"
                  :disabled="userInputs.global"
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="
                    userInputs.pullFrequency &&
                    errorState.isPullFrequencyValidationError &&
                    userInputs.pullFrequency !== '0'
                  "
                  :info="
                    $t(
                      'aws.createNewConfigguration.errorState.inValidPullFrequency',
                    )
                  "
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="errorState.isPullFrequencyValueError"
                  :info="$t('aws.rdsFormFields.errorState.noPullFrequency')"
                />
              </div>
            </div>

            <div class="monitoring-container">
              {{ $t('aws.rdsFormFields.enableMonitoring') }}
              <Toggle
                size="small"
                @onChange="handleMonitorToggeling"
                v-model:checked="userInputs.enableMonitoring"
              />
            </div>

            <div class="tags-container py-4">
              <div class="tag-condition-radio-button">
                <RexModalLabel :primaryLabel="$t('aws.rdsFormFields.tags')" />
                <RexRadioV3
                  class="tags-condition"
                  v-model="userInputs.tagsCondition"
                  :options="radioGroupOptions"
                ></RexRadioV3>
              </div>

              <div class="tags-inputs">
                <div
                  v-for="(currentTag, index) in userInputs.tags"
                  :key="index"
                  class="tag-input-group"
                >
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.key"
                    type="text"
                    id="input-tag-key"
                  />
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.value"
                    type="text"
                    id="input-tag-value"
                  />
                  <RexButtonV3
                    type="primary"
                    danger
                    v-if="index + 1 !== userInputs.tags.length"
                    variant="primary"
                    id="remove-tag"
                    @onClick="userInputs.tags.splice(index, 1)"
                    class="tag-button"
                  >
                    <BaseIcon height="15px" width="15px" name="deleteFilled" />
                  </RexButtonV3>
                  <RexButtonV3
                    v-if="index + 1 === userInputs.tags.length"
                    variant="primary"
                    id="add-tag"
                    @onClick="
                      currentTag.key &&
                        currentTag.value &&
                        userInputs.tags.push({})
                    "
                    class="tag-button"
                  >
                    <BaseIcon height="15px" width="15px" name="plus" />
                  </RexButtonV3>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <button
              @click="
                $emit('close', type === 'edit' ? 'edit' : 'manualInstall')
              "
              id="cancel"
              class="cancel-button"
            >
              {{ $t('aws.createNewConfigguration.cancel') }}
            </button>
            <RexButtonV3
              @onClick="handleSaveClick"
              id="save"
              :variant="
                isEqual(initialValue, userInputs) ? 'disabled' : 'primary'
              "
            >
              {{ $t('aws.createNewConfigguration.save') }}
            </RexButtonV3>
          </div>
        </div>
      </RexCardV3>
    </RexSpinV3>
  </div>
</template>

<style lang="scss" scoped>
.card-header-text {
  @include flex-center-vert;
  font-size: 14px;
  margin: 0;
  padding: 10px 0;
}

.lambda-create-edit-form {
  margin-top: 10px;
}

.form-container {
  @include flex-column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.input-container {
  @include flex-column;
  width: 100%;
  align-items: start;
}

.select-databases {
  @include flex-column;
}

.pull-frequency-container {
  @include flex-center-justify(space-between);
  width: 100%;
}

.toggle-container {
  @include flex-center-vert;
  gap: 5px;
  font-weight: bold;
}

.input-field-container {
  @include flex-column;
  width: 100%;
}

.monitoring-container {
  @include flex-center-vert;
  gap: 5px;
  margin-top: 10px;
  font-weight: bold;
}

.tags-container {
  @include flex-column;
  width: 100%;
}

.tag-condition-radio-button {
  @include flex-center-justify(space-between);
  width: 100%;
  font-weight: bold;
}

.tags-inputs {
  @include flex-column;
  gap: 7px;
  max-height: 6rem;
}

.tag-input-group {
  @include flex-center-justify(space-between);
  width: 100%;
}

.tag-input {
  width: 100%;
}

.tag-button {
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
}

.card-footer {
  @include flex-center-justify(flex-end);
  gap: 1rem;
  width: 100%;
}

.cancel-button {
  @include flex-center;
  background: none;
  border: none;
  cursor: pointer;
  color: white;
}

.card-body {
  @include flex-center-justify(space-between);
  flex-direction: column;
  height: 100%;
}
</style>
